// Server side of the public status page /status/$slug: the loader that reads the public status
// JSON before the page renders, and the head (title, description, robots) built from it.
// Uptrack's own page (slug `uptrack`, also served at https://status.uptrack.app/) is indexable and in
// the sitemap. A customer page keeps the `noindex` setting its owner chose in the API.
import { jsonLdScript, webPage } from '@/lib/schema'
import { StatusApiError, statusV2, type StatusPageData } from '@/lib/status-v2'

export const OWN_STATUS_SLUG = 'uptrack'
export const OWN_STATUS_URL = 'https://uptrack.app/status/uptrack'

const OWN_TITLE = 'Uptrack Status — Service Health and Incidents'
const OWN_DESCRIPTION = 'Live status of the Uptrack API, website and status page, with the services checked, recent incidents and planned maintenance.'
const OG_IMAGE = 'https://uptrack.app/og/home.png'

export type StatusLoad =
  | { state: 'ok'; page: StatusPageData; fetchedAt: number }
  | { state: 'not-found' }
  | { state: 'error' }

/** Reads the public status JSON. Never throws: a failed read gives `error` and the client page retries. */
export async function loadStatusPage(slug: string, read: (slug: string) => Promise<{ data: StatusPageData }> = statusV2.getStatusPage): Promise<StatusLoad> {
  try {
    const res = await read(slug)
    return res.data ? { state: 'ok', page: res.data, fetchedAt: Date.now() } : { state: 'not-found' }
  } catch (err) {
    return err instanceof StatusApiError && err.status === 404 ? { state: 'not-found' } : { state: 'error' }
  }
}

type Meta = Record<string, string>

/** Own title, own description, and a robots tag only where the page is not for search. */
export function statusPageHead(slug: string, load?: StatusLoad) {
  const page = load?.state === 'ok' ? load.page : undefined
  const own = slug === OWN_STATUS_SLUG
  // A page with a `noindex` flag, and a slug that does not exist, stay out of search. A failed read
  // says nothing about the flag, so it adds no tag (a short outage must not drop a page).
  const noindex = page ? Boolean(page.noindex) : load?.state === 'not-found'

  let title: string
  let description: string
  if (own) {
    title = OWN_TITLE
    description = OWN_DESCRIPTION
  } else if (page) {
    title = /status/i.test(page.name) ? page.name : `${page.name} Status`
    description = page.description?.trim() || `Current status of ${page.name}: the services it checks, recent incidents and planned maintenance.`
  } else {
    title = load?.state === 'not-found' ? 'Status page not found' : 'Status page'
    description = 'A public status page of a service that uses Uptrack.'
  }

  const meta: Meta[] = [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: OG_IMAGE },
    ...(own ? [{ property: 'og:url', content: OWN_STATUS_URL }] : []),
    ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
  ]
  return {
    meta,
    links: own ? [{ rel: 'canonical', href: OWN_STATUS_URL }] : [],
    scripts: own ? [jsonLdScript(webPage({ name: title, description, url: OWN_STATUS_URL }))] : [],
  }
}
